import classNames from 'classnames';
import PropTypes from 'prop-types';
import useLocalize from '../../utils/hooks/useLocalize';

/**
 * The two answers to the re-search prompt, in Figma order.
 *
 * Exported so the container can compare against the codes instead of bare
 * string literals, and so tests can drive the options without restating the
 * label keys.
 */
export const RE_SEARCH_CHOICES = Object.freeze({
  REUSE: 'yes',
  CHANGE: 'no'
});

const OPTIONS = [
  { code: RE_SEARCH_CHOICES.REUSE, labelKey: 'ai.scanner.research.option.yes' },
  { code: RE_SEARCH_CHOICES.CHANGE, labelKey: 'ai.scanner.research.option.no' }
];

/**
 * ReSearchOptions Component
 *
 * STORY-015 (#729): the 「はい」/「いいえ」 answers to the re-search prompt, shown
 * once a second round begins under the previous round's frozen Q1/Q2.
 *
 * Deliberately built from the SAME `ai-scanner-question-option*` markup and
 * classes as Q1/Q2 rather than a parallel set: Figma 5271:13023 (unselected)
 * and 5271:13170 (selected) are instances of the very `v4a
 * radio-button-chips` component those rules already implement, so a second
 * copy would be two places to keep in step with one design component.
 *
 * Presentational only — it owns no state. The container decides what
 * 「はい」 and 「いいえ」 mean (reuse the previous answers vs. ask Q1/Q2 again).
 *
 * @param {Object} props
 * @param {string|null} [props.value] - The chosen code, or `null` while the
 *   member has not answered.
 * @param {Function} props.onSelect - Called with the chosen code. Not called
 *   for a repeat tap on the current choice — re-running the round on a tap
 *   that changed nothing would discard results the member is reading.
 * @param {string} props.labelledBy - Id of the prompt these options answer,
 *   so the radio group is announced with the question rather than unlabelled.
 * @param {string} [props.instanceId] - Namespace for the radio group `name`.
 *   A radio group spans the whole document, so each round needs its own.
 * @returns {JSX.Element}
 */
export const ReSearchOptions = ({
  value = null,
  onSelect,
  labelledBy,
  instanceId = 'ai-scanner-research'
}) => {
  const localize = useLocalize();

  return (
    <fieldset
      className="ai-scanner-question-options ai-scanner-research-options"
      role="radiogroup"
      aria-labelledby={labelledBy}
    >
      {OPTIONS.map((option) => {
        const isSelected = value === option.code;
        return (
          <label
            key={option.code}
            className={classNames('ai-scanner-question-option', {
              'ai-scanner-question-option--selected': isSelected
            })}
          >
            <input
              type="radio"
              className="ai-scanner-question-option-input"
              name={`${instanceId}-research`}
              value={option.code}
              checked={isSelected}
              onChange={() => {
                if (isSelected) return;
                onSelect(option.code);
              }}
            />
            <span
              className="ai-scanner-question-option-mark"
              aria-hidden="true"
            />
            <span className="ai-scanner-question-option-label">
              {localize(option.labelKey)}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
};

ReSearchOptions.propTypes = {
  value: PropTypes.oneOf([
    RE_SEARCH_CHOICES.REUSE,
    RE_SEARCH_CHOICES.CHANGE,
    null
  ]),
  onSelect: PropTypes.func.isRequired,
  labelledBy: PropTypes.string.isRequired,
  instanceId: PropTypes.string
};

export default ReSearchOptions;
